import { androidBridge, appleBridge, bridgeFor, windowsBridge } from "./Bridge"
import { logger } from "./Logger"
import { Localization } from "./Localization"
import { Platform, deriveAppStyles } from "./Platform"
import type { ISessionResponse } from "../Types"
import { IURLNavigationDestination } from "../Types/Generated/AppBaseWebView/URLNavigation"
import {
  AppleBridgeManifestCapabilities,
  AppleBridgeManifestRequests,
  AndroidBridgeManifestRequests,
  WindowsBridgeManifestRequests,
} from "../Types/Generated/Bridge/NativeBridge"

// Messages migrate onto app-base's window.ab one at a time: every legacy
// per-platform shape stays live and untouched underneath until the shell that
// serves it moves. Nothing here is renamed or deleted.
//
// A plain browser, cloud or catalog session has no bridge at all, and app-base's
// null client behaves identically to its absence — `supports()` false for
// everything — which is what lets the fallback below be one code path rather
// than a chain of `window.webkit &&` guards.

// What the server did with a StoreKit entitlement the native shell posted to
// /api/account/appstore/sync (mirrors Swift's AppStoreSyncOutcome, plus "none" for
// "the device holds no entitlement, or the bridge/request never got an answer").
export type AppStoreSyncOutcome = "activated" | "claimed" | "notActive" | "linkedToAnotherAccount" | "ignored" | "none";

export interface AppStoreSyncResult {
  outcome: AppStoreSyncOutcome;
  // Which Logbook account holds (or held) the subscription, for the
  // linkedToAnotherAccount and claimed outcomes.
  accountName?: string;
}

// Shared plumbing for the three App Store entitlement bridges. Returns undefined when
// there is no native bridge (a browser session), so callers can treat the whole feature
// as unavailable rather than showing a failed action.
async function postAppStoreMessage(name: string): Promise<AppStoreSyncResult | undefined> {
  const bridge = bridgeFor(name);
  if (bridge) {
    try {
      return await bridge.call<AppStoreSyncResult>(name);
    } catch (err) {
      logger.debug(`${name} bridge error`, err);
      return { outcome: "none" };
    }
  }
  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers[name]) {
    try {
      const response = await window.webkit.messageHandlers[name].postMessage(name);
      return response as AppStoreSyncResult;
    } catch (err) {
      logger.debug(`${name} bridge error`, err);
      return { outcome: "none" };
    }
  }
  return undefined;
}

function postBridgeMessage(name: string, payload?: unknown): boolean {
  const bridge = bridgeFor(name);
  if (!bridge) return false;
  const posted = payload === undefined ? bridge.post(name) : bridge.post(name, payload);
  void posted.catch((err) => logger.debug(`${name} bridge error`, err));
  return true;
}

function availableAppleNativeSignInBridge() {
  const bridge = appleBridge();
  return bridge?.supports(AppleBridgeManifestRequests.nativeSignIn)
    && bridge.capability(AppleBridgeManifestCapabilities.nativeSignIn) === "available"
    ? bridge
    : undefined;
}

declare global {
  interface Window {
    webkit?: {
      messageHandlers: {
        [x: string]: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
          postMessage: (data: string) => Promise<any>;
        };
      };
    };
    // WebView2 (Windows shell) host-message channel. The shell listens on
    // `CoreWebView2.WebMessageReceived`; we post a JSON string tagged with a
    // `type` discriminator.
    chrome?: {
      webview?: {
        postMessage: (data: string) => void;
      };
    };
    // Injected before page scripts by the Windows shell (WebView2
    // AddScriptToExecuteOnDocumentCreated) so appStyles is available synchronously
    // at mount, matching the iOS/Mac webkit bridge.
    __appStyles?: string[];
    // Exposed by the Android shell via addJavascriptInterface; `value()` returns the
    // appStyles as a JSON array string (e.g. `["tablet","android"]`).
    __logbookAppStyles?: {
      value: () => string;
    };
    // The device's UI language, published by the native shells so the web app's
    // first render is already localized. Two shapes under one name: the Apple
    // (WKUserScript) and Windows (AddScriptToExecuteOnDocumentCreated) shells
    // inject a plain string at document start, while Android binds an object
    // via addJavascriptInterface — the same split as `__appStyles` vs
    // `__logbookAppStyles` above. Resolved by `Native.deviceLanguage`.
    __logbookDeviceLanguage?: string | { value: () => string };
    // Exposed by the Android shell via addJavascriptInterface for native
    // notification + alarm delivery (foreground service + high-importance channel).
    __logbookNotifications?: {
      show: (id: string, title: string, body: string) => void;
      playAlarm: (id: string) => void;
      stopAlarm: (id: string) => void;
    };
    // Exposed by the Android shell via addJavascriptInterface. Android's WebView does
    // not implement `window.print()` itself (unlike a desktop browser or the
    // WKWebView/WebView2 shells), so printing is wired up natively via
    // `WebView.createPrintDocumentAdapter` + `android.print.PrintManager`, which raises
    // the system print/"Save as PDF" dialog for the currently rendered page.
    __logbookPrint?: {
      print: () => void;
    };
    // Exposed by the Android shell via addJavascriptInterface (issue #405): mirrors
    // `Logger.ts` output into the native capped log file, one JNI hop at a time,
    // mirroring the `updatePushToken`/`onLocationChanged` native bridge shape. `level`
    // is the `LogLevel` string, `text` the already-formatted `[category] message...`
    // line (see `Logger.ts`'s `postToNative`).
    __logbookLog?: {
      log: (level: string, text: string) => void;
    };
  }
}

// A plain-browser fallback alarm: loops the bundled `alarm.wav` (the same single
// source the native shells bundle, see Sources/LogbookSounds), keyed by alert id so
// several alarms can sound and stop independently. No-op when the Audio API is
// unavailable (e.g. SSR).
import alarmSoundUrl from "../alarm.wav"

const browserAlarms: { [id: string]: HTMLAudioElement } = {};
const browserNativeSignInPath = "/api/session/native-sign-in/browser";
const browserNativeSignInMessage = "logbook-native-sign-in";

interface BrowserNativeSignInStartResponse {
  loginUrl: string;
  callbackOrigin: string;
}

interface BrowserNativeSignInMessage {
  type: string;
  code: string;
  state: string;
}

function nativeSignInState(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function startBrowserNativeSignIn(): Promise<ISessionResponse | undefined> {
  const popup = window.open("", "logbook-native-sign-in", "popup,width=520,height=720");
  if (!popup) {
    return undefined;
  }

  const state = nativeSignInState();
  try {
    const start = await fetch(`${browserNativeSignInPath}/start`, {
      method: "POST",
      headers: { "Accept": "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ state, origin: window.location.origin }),
    });
    if (!start.ok) {
      popup.close();
      return undefined;
    }
    const config = await start.json() as BrowserNativeSignInStartResponse;
    popup.location.href = config.loginUrl;

    return await new Promise<ISessionResponse | undefined>((resolve) => {
      const timeout = window.setTimeout(() => finish(undefined), 130_000);
      const finish = (response: ISessionResponse | undefined) => {
        window.clearTimeout(timeout);
        window.removeEventListener("message", receive);
        popup.close();
        resolve(response);
      };
      const receive = async (event: MessageEvent<BrowserNativeSignInMessage>) => {
        const message = event.data;
        if (
          event.source !== popup ||
          event.origin !== config.callbackOrigin ||
          message?.type !== browserNativeSignInMessage ||
          message.state !== state ||
          !message.code
        ) {
          return;
        }
        window.removeEventListener("message", receive);
        try {
          const exchange = await fetch(`${browserNativeSignInPath}/exchange`, {
            method: "POST",
            headers: { "Accept": "application/json", "Content-Type": "application/json" },
            body: JSON.stringify({ code: message.code, state }),
          });
          finish(exchange.ok ? await exchange.json() as ISessionResponse : undefined);
        } catch (err) {
          logger.debug("browser nativeSignIn exchange error", err);
          finish(undefined);
        }
      };
      window.addEventListener("message", receive);
    });
  } catch (err) {
    logger.debug("browser nativeSignIn error", err);
    popup.close();
    return undefined;
  }
}

function startBrowserAlarm(id: string): void {
  if (browserAlarms[id]) return;
  if (typeof Audio === "undefined") return;
  try {
    const audio = new Audio(alarmSoundUrl);
    audio.loop = true;
    browserAlarms[id] = audio;
    void audio.play().catch((err) => logger.debug("browser alarm play blocked", err));
  } catch (err) {
    logger.debug("browser alarm error", err);
  }
}

function stopBrowserAlarm(id: string): void {
  const audio = browserAlarms[id];
  if (audio) {
    try { audio.pause(); audio.currentTime = 0; } catch { /* already stopped */ }
    delete browserAlarms[id];
  }
}

/**
 * Thin wrapper around the native shell bridges (iOS/Mac `webkit`, Windows
 * WebView2 `chrome.webview`, Android `__logbookNotifications`/`__logbookPrint`).
 * Isolated from React so the notification / alarm / restart / app-styles / print
 * handshakes have a single home; each method is a safe no-op (or plain-browser
 * fallback) when no native bridge is present.
 */
function printableDocumentHTML(): string {
  const documentElement = document.documentElement.cloneNode(true) as HTMLElement;
  let head = documentElement.querySelector("head");
  if (!head) {
    head = document.createElement("head");
    documentElement.prepend(head);
  }
  const base = head.querySelector("base") ?? document.createElement("base");
  base.setAttribute("href", `${window.location.origin}/`);
  head.prepend(base);
  return documentElement.outerHTML;
}

export const Native = {
  hasAppleManifest(): boolean {
    return appleBridge() !== undefined;
  },

  async openAppleURL(url: string): Promise<void> {
    const bridge = appleBridge();
    if (!bridge?.supports(AppleBridgeManifestRequests["webView.openURL"])
      || bridge.capability(AppleBridgeManifestCapabilities["webView.urlNavigation.acknowledged"]) !== "available") {
      throw new Error("Acknowledged native URL opening is unavailable");
    }
    await bridge.call(AppleBridgeManifestRequests["webView.openURL"], {
      url,
      destination: IURLNavigationDestination.overlay,
    });
  },

  /**
   * Triggers the platform's print flow for the currently rendered printable content,
   * using the advertised native print bridge, then legacy handlers or browser printing.
   * Native replies arrive when the user finishes the print flow, not when it opens.
   */
  print(app: { appStyles: string[] }): void {
    const title = document.title || undefined;
    const legacyFallback = () => {
      if (window.webkit?.messageHandlers?.printAction && Platform.isIOS(app)) {
        window.webkit.messageHandlers.printAction.postMessage("");
      } else if (window.__logbookPrint) {
        window.__logbookPrint.print();
      } else {
        print();
      }
    };
    const apple = appleBridge();
    if (apple?.supports(AppleBridgeManifestRequests.printAction)) {
      void apple.post(
        AppleBridgeManifestRequests.printAction,
        { content: "", title },
        { timeoutMilliseconds: null },
      ).catch((err) => {
        logger.error("printAction bridge error", err);
      });
      return;
    }
    const payload = {
      // Android prints in a detached WebView. The explicit base keeps the
      // current app and print stylesheets resolvable from the local server.
      content: printableDocumentHTML(),
      title,
    };
    const android = androidBridge();
    if (android?.supports(AndroidBridgeManifestRequests.printAction)) {
      void android.post(
        AndroidBridgeManifestRequests.printAction,
        payload,
        { timeoutMilliseconds: null },
      ).catch((err) => {
        logger.error("printAction bridge error", err);
      });
      return;
    }
    const bridge = bridgeFor("printAction");
    if (bridge) {
      void bridge.post("printAction", payload, { timeoutMilliseconds: null }).catch((err) => {
        logger.error("printAction bridge error", err);
      });
      return;
    }
    legacyFallback();
  },
  showNativeNotification(id: string, title: string, body: string): void {
    try {
      const apple = appleBridge();
      if (apple?.supports(AppleBridgeManifestRequests.showNotification)) {
        void apple.post(
          AppleBridgeManifestRequests.showNotification,
          { id, title, body },
        ).catch((err) => logger.debug("showNotification bridge error", err));
        return;
      }
      const android = androidBridge();
      if (android?.supports(AndroidBridgeManifestRequests.showNotification)) {
        void android.post(
          AndroidBridgeManifestRequests.showNotification,
          { id, title, body },
        ).catch((err) => logger.debug("showNotification bridge error", err));
        return;
      }
      const windows = windowsBridge();
      if (windows?.supports(WindowsBridgeManifestRequests.showNotification)) {
        void windows.post(
          WindowsBridgeManifestRequests.showNotification,
          { id, title, body },
        ).catch((err) => logger.debug("showNotification bridge error", err));
        return;
      }
      if (postBridgeMessage("showNotification", { id, title, body })) {
        return;
      }
      if (window.webkit?.messageHandlers?.showNotification) {
        window.webkit.messageHandlers.showNotification.postMessage(JSON.stringify({ id: id, title: title, body: body }));
      } else if (window.chrome?.webview) {
        window.chrome.webview.postMessage(JSON.stringify({ type: "showNotification", id: id, title: title, body: body }));
      } else if (window.__logbookNotifications) {
        window.__logbookNotifications.show(id, title, body);
      } else if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        // Plain browser: a best-effort Web Notification.
        new Notification(title, { body, tag: id });
      }
    } catch (err) {
      // `String(err)` rather than the error object: Logger's `safeStringify` runs
      // JSON.stringify, and an Error has no enumerable own properties, so it would
      // serialize to "{}" and the message would be lost on the way to native.
      logger.debug("showNativeNotification error", String(err));
    }
  },
  /**
   * Start the looping audible alarm for alert `id` on whichever native shell is
   * hosting us, falling back to a WebAudio beep in a plain browser. Idempotent.
   */
  playAlarm(id: string): void {
    try {
      const apple = appleBridge();
      if (apple?.supports(AppleBridgeManifestRequests.playAlarm)) {
        void apple.post(
          AppleBridgeManifestRequests.playAlarm,
          { id },
        ).catch((err) => logger.debug("playAlarm bridge error", err));
        return;
      }
      const android = androidBridge();
      if (android?.supports(AndroidBridgeManifestRequests.playAlarm)) {
        void android.post(
          AndroidBridgeManifestRequests.playAlarm,
          { id },
        ).catch((err) => logger.debug("playAlarm bridge error", err));
        return;
      }
      const windows = windowsBridge();
      if (windows?.supports(WindowsBridgeManifestRequests.playAlarm)) {
        void windows.post(
          WindowsBridgeManifestRequests.playAlarm,
          { id },
        ).catch((err) => logger.debug("playAlarm bridge error", err));
        return;
      }
      if (postBridgeMessage("playAlarm", { id })) {
        return;
      }
      if (window.webkit?.messageHandlers?.playAlarm) {
        window.webkit.messageHandlers.playAlarm.postMessage(JSON.stringify({ id: id }));
      } else if (window.chrome?.webview) {
        window.chrome.webview.postMessage(JSON.stringify({ type: "playAlarm", id: id }));
      } else if (window.__logbookNotifications) {
        window.__logbookNotifications.playAlarm(id);
      } else {
        startBrowserAlarm(id);
      }
    } catch (err) {
      logger.debug("playAlarm error", err);
    }
  },
  /** Stop the audible alarm for alert `id` (ack / snooze / clear / resolve). */
  stopAlarm(id: string): void {
    try {
      const apple = appleBridge();
      if (apple?.supports(AppleBridgeManifestRequests.stopAlarm)) {
        void apple.post(
          AppleBridgeManifestRequests.stopAlarm,
          { id },
        ).catch((err) => logger.debug("stopAlarm bridge error", err));
        return;
      }
      const android = androidBridge();
      if (android?.supports(AndroidBridgeManifestRequests.stopAlarm)) {
        void android.post(
          AndroidBridgeManifestRequests.stopAlarm,
          { id },
        ).catch((err) => logger.debug("stopAlarm bridge error", err));
        return;
      }
      const windows = windowsBridge();
      if (windows?.supports(WindowsBridgeManifestRequests.stopAlarm)) {
        void windows.post(
          WindowsBridgeManifestRequests.stopAlarm,
          { id },
        ).catch((err) => logger.debug("stopAlarm bridge error", err));
        return;
      }
      if (postBridgeMessage("stopAlarm", { id })) {
        return;
      }
      if (window.webkit?.messageHandlers?.stopAlarm) {
        window.webkit.messageHandlers.stopAlarm.postMessage(JSON.stringify({ id: id }));
      } else if (window.chrome?.webview) {
        window.chrome.webview.postMessage(JSON.stringify({ type: "stopAlarm", id: id }));
      } else if (window.__logbookNotifications) {
        window.__logbookNotifications.stopAlarm(id);
      } else {
        stopBrowserAlarm(id);
      }
    } catch (err) {
      logger.debug("stopAlarm error", err);
    }
  },
  /**
   * Toggle the native background **network keep-alive** for a companion device so a
   * backgrounded / locked crew phone stays resident on the LAN and keeps receiving the
   * alert fan-out (and can sound the alarm). Decoupled from `nativeNMEAGPS`: keep-alive
   * only keeps the app alive; NMEA-GPS additionally publishes the location fixes.
   * Real on iOS; Android is already covered by the always-on foreground service, and
   * Mac/Windows are not suspended the same way, so those shells no-op. Safe no-op in a
   * plain browser or when the hosting shell registers no handler.
   */
  setNetworkKeepAlive(active: boolean): void {
    try {
      const apple = appleBridge();
      if (apple?.supports(AppleBridgeManifestRequests.setNetworkKeepAlive)) {
        void apple.post(
          AppleBridgeManifestRequests.setNetworkKeepAlive,
          { active },
        ).catch((err) => logger.debug("setNetworkKeepAlive bridge error", err));
        return;
      }
      if (postBridgeMessage("setNetworkKeepAlive", { active })) {
        return;
      }
      if (window.webkit?.messageHandlers?.setNetworkKeepAlive) {
        window.webkit.messageHandlers.setNetworkKeepAlive.postMessage(JSON.stringify({ active: active }));
      } else if (window.chrome?.webview) {
        window.chrome.webview.postMessage(JSON.stringify({ type: "setNetworkKeepAlive", active: active }));
      }
    } catch (err) {
      logger.debug("setNetworkKeepAlive error", err);
    }
  },
  async restartNative(): Promise<boolean> {
    const bridge = bridgeFor("restart");
    if (bridge) {
      try {
        logger.debug("restarting native");
        const response = await bridge.call<unknown>("restart");
        const restarted = response == true;
        logger.debug("native restart succeeded", restarted);
        return restarted;
      } catch (err) {
        logger.debug("bridge restart error", err);
        return false;
      }
    }
    try {
      if (window.webkit?.messageHandlers?.restart) {
        logger.debug("restarting native");
        const response = await window.webkit.messageHandlers.restart.postMessage("restart");
        // The outcome, never the response itself: these payloads can carry a session
        // id and an email (see CredentialLogging.test.ts, #1273).
        const restarted = response == true;
        logger.debug("native restart succeeded", restarted);
        return restarted;
      }
    } catch (err) {
      logger.debug("webkit restart error", err);
    }
    return false;
  },
  // Resolves the app-style overrides for the current runtime, in priority order:
  //   0. `window.ab`, when the shell exposes the `appStyles` message -- the one
  //      transport, from which the pair is DERIVED from the two device axes
  //      rather than delivered as a list (#1376),
  //   1. the iOS/Mac `webkit` bridge (async reply),
  //   2. the `window.__appStyles` global injected before page scripts by the
  //      Windows shell (WebView2 AddScriptToExecuteOnDocumentCreated),
  //   3. the Android `__logbookAppStyles` JS interface (JSON array string),
  //   4. a legacy `navigator.userAgent` Android sniff as a fallback,
  // returning `undefined` in a plain browser (leave the caller's default in place).
  //
  // Paths 1-4 are untouched and remain the only live ones until a shell exposes
  // the bridge, which is what "coexistence by message" means in practice.
  async loadAppStyles(): Promise<string[] | undefined> {
    const apple = appleBridge();
    if (apple?.supports(AppleBridgeManifestRequests.appStyles)) {
      const { formFactor, platform } = apple.values;
      const styles = deriveAppStyles({ formFactor, platform });
      logger.debug("loaded AppStyles from the Apple bridge", styles);
      return styles;
    }
    const android = androidBridge();
    if (android?.supports(AndroidBridgeManifestRequests.appStyles)) {
      const { formFactor, platform } = android.values;
      const styles = deriveAppStyles({ formFactor, platform });
      logger.debug("loaded AppStyles from the Android bridge", styles);
      return styles;
    }
    const windows = windowsBridge();
    if (windows?.supports(WindowsBridgeManifestRequests.appStyles)) {
      const { formFactor, platform } = windows.values;
      const styles = deriveAppStyles({ formFactor, platform });
      logger.debug("loaded AppStyles from the Windows bridge", styles);
      return styles;
    }
    const ab = bridgeFor("appStyles");
    if (ab) {
      // Both axes or nothing: half an answer is not an answer, and inventing the
      // missing half is the mistake `appStyles` made in the first place.
      const formFactor = ab.values.formFactor;
      const platform = ab.values.platform;
      if (typeof formFactor === "string" && typeof platform === "string") {
        const styles = deriveAppStyles({ formFactor, platform });
        logger.debug("loaded AppStyles from the bridge", styles);
        return styles;
      }
      logger.debug("bridge exposes appStyles but reported no device axes");
    }
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.appStyles) {
      try {
        const response = await window.webkit.messageHandlers.appStyles.postMessage("appStyles");
        const styles = response as string[];
        logger.debug("loaded AppStyles", styles);
        return styles;
      } catch (err) {
        logger.debug("webkit error", err);
        return undefined;
      }
    } else if (Array.isArray(window.__appStyles)) {
      logger.debug("loaded AppStyles from global", window.__appStyles);
      return window.__appStyles;
    } else if (window.__logbookAppStyles && typeof window.__logbookAppStyles.value === "function") {
      try {
        const styles = JSON.parse(window.__logbookAppStyles.value()) as string[];
        logger.debug("loaded AppStyles from native interface", styles);
        return styles;
      } catch (err) {
        logger.debug("native appStyles interface error", err);
        return undefined;
      }
    } else if (navigator.userAgent.toLowerCase().indexOf("android") >= 0) {
      return ["android"];
    }
    return undefined;
  },
  // The device's UI language, resolved to one the app has translations for, or
  // `undefined` when the device asks for nothing we can serve.
  //
  // Read synchronously from the generated Apple/Android/Windows manifest
  // value (matching `formFactor`/`platform`) or, for the not-yet-migrated
  // legacy shells, the `window.__logbookDeviceLanguage` global published
  // before page scripts run. Unlike `loadAppStyles`, this must be available
  // at the first paint or the user sees a frame of English.
  //
  // A plain browser or cloud session has no shell, so `navigator.languages`
  // decides. Whatever the source, the value is re-resolved here rather than
  // trusted, so a raw platform tag (`de-CH`) or an unsupported language can
  // never reach the `Localization.Languages` lookup.
  deviceLanguage(): string | undefined {
    const apple = appleBridge();
    if (apple?.supports(AppleBridgeManifestRequests.deviceLanguage)) {
      return Localization.resolve([apple.values.deviceLanguage]);
    }
    const android = androidBridge();
    if (android?.supports(AndroidBridgeManifestRequests.deviceLanguage)) {
      return Localization.resolve([android.values.deviceLanguage]);
    }
    const windows = windowsBridge();
    if (windows?.supports(WindowsBridgeManifestRequests.deviceLanguage)) {
      return Localization.resolve([windows.values.deviceLanguage]);
    }
    const native = window.__logbookDeviceLanguage;
    if (typeof native === "string") {
      return Localization.resolve([native]);
    }
    if (native && typeof native.value === "function") {
      try {
        return Localization.resolve([native.value()]);
      } catch (err) {
        logger.debug("native deviceLanguage interface error", err);
        return undefined;
      }
    }
    return Localization.resolve(Array.from(navigator.languages ?? []));
  },
  // Fetches the native "skipper" IAP subscription's price for display in the app's own
  // purchase flow (App Store guideline 3.1.2(c) requires the billed amount to be shown
  // in the app, not only in the system StoreKit sheet). The iOS bridge resolves this via
  // StoreKit's `Product.displayPrice`, which is already formatted/localized for the
  // user's App Store storefront -- so no local per-currency price table is needed here.
  //
  // `storefront` (an ISO 3166-1 alpha-3 country code, e.g. "USA"/"DEU") is what actually
  // decides the billing currency, and the bridge reports it even when the price lookup
  // failed -- so the caller can still pick the right fallback currency instead of
  // guessing from the device language, which is wrong for anyone whose App Store region
  // differs from their language.
  //
  // Returns undefined (a safe no-op for the caller, which falls back to its default)
  // when there is no bridge, or the native call fails.
  async loadIAPProductInfo(): Promise<{ price?: string; storefront?: string; currency?: string } | undefined> {
    const bridge = bridgeFor("iapProductInfo");
    if (bridge) {
      try {
        return await bridge.call<{ price?: string; storefront?: string; currency?: string }>("iapProductInfo");
      } catch (err) {
        logger.debug("iapProductInfo bridge error", err);
        return undefined;
      }
    }
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.iapProductInfo) {
      try {
        const response = await window.webkit.messageHandlers.iapProductInfo.postMessage("iapProductInfo");
        return response as { price?: string; storefront?: string; currency?: string };
      } catch (err) {
        logger.debug("iapProductInfo bridge error", err);
        return undefined;
      }
    }
    return undefined;
  },
  // Reconcile the entitlements the device already holds with the signed-in account,
  // without touching the App Store (no `AppStore.sync()`, so no sign-in prompt). Used to
  // find out, on opening the subscription page, whether this Apple ID's subscription is
  // already linked to a *different* Logbook account — in which case buying again is
  // impossible (Apple never sells the same auto-renewing product twice to one Apple ID)
  // and the page must explain that instead of offering Subscribe.
  async loadAppStoreEntitlementState(): Promise<AppStoreSyncResult | undefined> {
    return postAppStoreMessage("appStoreEntitlementState");
  },
  // App Store guideline 3.1.1: an explicit, user-initiated restore. Re-fetches the Apple
  // ID's transaction history natively and re-posts every entitlement to the server,
  // reporting what happened so the caller can react (restored, nothing found, or held by
  // another Logbook account).
  async restorePurchases(): Promise<AppStoreSyncResult | undefined> {
    return postAppStoreMessage("restorePurchases");
  },
  // Move a subscription held by another Logbook account to the one signed in now. Only
  // called after the user confirms, since the other account loses access.
  async claimAppStoreSubscription(): Promise<AppStoreSyncResult | undefined> {
    return postAppStoreMessage("claimSubscription");
  },
  performIAP(plan: string, token: string | undefined): void {
    if (postBridgeMessage("performIAP", { plan, token })) {
      return;
    }
    window.webkit?.messageHandlers?.performIAP?.postMessage(JSON.stringify({ plan, token }));
  },
  manageSubscriptions(): void {
    if (postBridgeMessage("manageSubscriptions")) {
      return;
    }
    window.webkit?.messageHandlers?.manageSubscriptions?.postMessage("manageSubscriptions");
  },
  async startNativeSignIn(): Promise<ISessionResponse | undefined> {
    const apple = availableAppleNativeSignInBridge();
    if (apple) {
      try {
        // Safari owns this interactive lifetime, not the bridge's RPC deadline.
        return await apple.call(AppleBridgeManifestRequests.nativeSignIn, undefined, { timeoutMilliseconds: null });
      } catch (err) {
        logger.debug("nativeSignIn bridge error", err);
        return undefined;
      }
    }
    const bridge = bridgeFor("nativeSignIn");
    if (bridge) {
      try {
        return await bridge.call<ISessionResponse>("nativeSignIn", undefined, { timeoutMilliseconds: null });
      } catch (err) {
        logger.debug("nativeSignIn bridge error", err);
        return undefined;
      }
    }
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeSignIn) {
      try {
        const response = await window.webkit.messageHandlers.nativeSignIn.postMessage("nativeSignIn");
        return response as ISessionResponse;
      } catch (err) {
        logger.debug("nativeSignIn bridge error", err);
        return undefined;
      }
    }
    return startBrowserNativeSignIn();
  },
  async nativeSignInAvailable(): Promise<boolean> {
    if (availableAppleNativeSignInBridge()) {
      return true;
    }
    if (bridgeFor("nativeSignIn")) {
      return true;
    }
    if (window.webkit?.messageHandlers?.nativeSignIn) {
      return true;
    }
    try {
      const response = await fetch(browserNativeSignInPath, {
        method: "GET",
        headers: { "Accept": "application/json" },
      });
      if (!response.ok) {
        return false;
      }
      const config = await response.json() as { enabled?: boolean };
      return config.enabled === true;
    } catch (err) {
      logger.debug("browser nativeSignIn availability error", err);
      return false;
    }
  },

  hasNativeSignIn(): boolean {
    return !!availableAppleNativeSignInBridge()
      || !!bridgeFor("nativeSignIn")
      || !!window.webkit?.messageHandlers?.nativeSignIn;
  },
};
